/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Dimensions,
  TextInput,
  Modal,
  ActivityIndicator,
  Platform,
  Linking,
  Image,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  Keyboard,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system/legacy';
import * as ImagePicker from 'expo-image-picker';

const { width } = Dimensions.get('window');

const InternshipsApply = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { internship } = route.params || {};
  
  const [isOnline, setIsOnline] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  const [applicationForm, setApplicationForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    university: '',
    major: '',
    graduationYear: '',
    gpa: '',
    linkedin: '',
    github: '',
    portfolio: '',
    coverLetter: '',
    whyThisCompany: '',
    relevantExperience: '',
    skills: '',
    availability: '',
    salaryExpectations: '',
    references: '',
    additionalInfo: '',
  });

  const [documents, setDocuments] = useState({
    resume: null,
    transcript: null,
    portfolioFile: null,
    coverLetterFile: null,
    otherDocuments: [],
  });

  const [formErrors, setFormErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const scrollViewRef = useRef(null);
  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const phoneInputRef = useRef(null);

  useEffect(() => {
    loadInitialData();
    setupNetInfoListener();
    loadSavedApplication();
  }, []);

  const setupNetInfoListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
    });
    return unsubscribe;
  };

  const loadInitialData = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadSavedApplication = async () => {
    try {
      const saved = await AsyncStorage.getItem('draftApplication');
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.internshipId === internship?.id) {
          setApplicationForm(draft.form);
          setDocuments(draft.documents || documents);
          Alert.alert(
            'Draft Loaded',
            'Your previously saved draft has been loaded.',
            [{ text: 'OK' }]
          );
        }
      }
    } catch (error) {
      console.error('Error loading saved application:', error);
    }
  };

  const saveDraft = async () => {
    try {
      const draft = {
        internshipId: internship?.id,
        internshipTitle: internship?.title,
        form: applicationForm,
        documents: documents,
        timestamp: new Date().toISOString(),
      };
      await AsyncStorage.setItem('draftApplication', JSON.stringify(draft));
      Alert.alert('Draft Saved', 'Your application has been saved as a draft.', [
        { text: 'OK' }
      ]);
    } catch (error) {
      console.error('Error saving draft:', error);
      Alert.alert('Error', 'Failed to save draft. Please try again.');
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Step 1: Personal Information
    if (currentStep === 1) {
      if (!applicationForm.fullName.trim()) errors.fullName = 'Full name is required';
      if (!applicationForm.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applicationForm.email)) {
        errors.email = 'Please enter a valid email';
      }
      if (!applicationForm.phone.trim()) errors.phone = 'Phone number is required';
      if (!applicationForm.university.trim()) errors.university = 'University is required';
      if (!applicationForm.major.trim()) errors.major = 'Major is required';
      if (!applicationForm.graduationYear.trim()) errors.graduationYear = 'Graduation year is required';
    }
    
    // Step 2: Documents
    if (currentStep === 2) {
      if (!documents.resume) errors.resume = 'Resume is required';
    }
    
    // Step 3: Professional Details
    if (currentStep === 3) {
      if (!applicationForm.coverLetter.trim()) errors.coverLetter = 'Cover letter is required';
      if (!applicationForm.whyThisCompany.trim()) errors.whyThisCompany = 'This field is required';
      if (!applicationForm.relevantExperience.trim()) errors.relevantExperience = 'This field is required';
      if (!applicationForm.skills.trim()) errors.skills = 'Skills are required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (!validateForm()) {
      Alert.alert('Validation Error', 'Please fill in all required fields correctly.');
      return;
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      scrollViewRef.current?.scrollTo({ x: 0, y: 0, animated: true });
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      scrollViewRef.current?.scrollTo({ x: 0, y: 0, animated: true });
    }
  };

  const pickDocument = async (type) => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        copyToCacheDirectory: true,
      });

      if (result.type === 'success') {
        const fileInfo = await FileSystem.getInfoAsync(result.uri);
        
        if (fileInfo.size > 10 * 1024 * 1024) { // 10MB limit
          Alert.alert('File Too Large', 'Please select a file smaller than 10MB.');
          return;
        }

        const document = {
          uri: result.uri,
          name: result.name,
          size: fileInfo.size,
          type: result.mimeType,
          uploadedAt: new Date().toISOString(),
        };

        setDocuments(prev => ({
          ...prev,
          [type]: document,
        }));

        Alert.alert('Success', `${result.name} uploaded successfully.`);
      }
    } catch (error) {
      console.error('Error picking document:', error);
      Alert.alert('Error', 'Failed to upload document. Please try again.');
    }
  };

  const removeDocument = (type) => {
    Alert.alert(
      'Remove Document',
      'Are you sure you want to remove this document?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            setDocuments(prev => ({
              ...prev,
              [type]: null,
            }));
          },
        },
      ]
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      Alert.alert('Validation Error', 'Please fill in all required fields correctly.');
      return;
    }

    setSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Save application to history
      const applicationHistory = await AsyncStorage.getItem('applicationHistory');
      const history = applicationHistory ? JSON.parse(applicationHistory) : [];
      
      const newApplication = {
        id: Date.now().toString(),
        internshipId: internship?.id,
        internshipTitle: internship?.title,
        company: internship?.company,
        appliedDate: new Date().toISOString(),
        status: 'submitted',
        form: applicationForm,
        documents: documents,
      };

      history.unshift(newApplication);
      await AsyncStorage.setItem('applicationHistory', JSON.stringify(history));

      // Clear draft
      await AsyncStorage.removeItem('draftApplication');

      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      Alert.alert('Submission Error', 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderHeader = () => (
    <LinearGradient
      colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        <Text style={styles.headerTitle}>✍️ Apply for Internship</Text>
        <Text style={styles.headerSubtitle}>
          Step {currentStep} of {totalSteps}
        </Text>
      </View>
      <TouchableOpacity 
        style={styles.saveButton}
        onPress={saveDraft}
      >
        <MaterialCommunityIcons name="content-save" size={22} color="#ffffff" />
      </TouchableOpacity>
    </LinearGradient>
  );

  const renderProgressBar = () => (
    <View style={styles.progressContainer}>
      <View style={styles.progressBar}>
        {[...Array(totalSteps)].map((_, index) => (
          <View key={index} style={styles.stepContainer}>
            <View style={[
              styles.stepCircle,
              currentStep > index + 1 ? styles.stepCompleted : 
              currentStep === index + 1 ? styles.stepActive : styles.stepInactive
            ]}>
              {currentStep > index + 1 ? (
                <MaterialCommunityIcons name="check" size={16} color="#ffffff" />
              ) : (
                <Text style={[
                  styles.stepNumber,
                  currentStep === index + 1 ? styles.stepNumberActive : styles.stepNumberInactive
                ]}>
                  {index + 1}
                </Text>
              )}
            </View>
            {index < totalSteps - 1 && (
              <View style={[
                styles.stepLine,
                currentStep > index + 1 ? styles.stepLineCompleted : styles.stepLineInactive
              ]} />
            )}
          </View>
        ))}
      </View>
      <View style={styles.stepLabels}>
        <Text style={[styles.stepLabel, currentStep === 1 && styles.stepLabelActive]}>
          Personal
        </Text>
        <Text style={[styles.stepLabel, currentStep === 2 && styles.stepLabelActive]}>
          Documents
        </Text>
        <Text style={[styles.stepLabel, currentStep === 3 && styles.stepLabelActive]}>
          Professional
        </Text>
        <Text style={[styles.stepLabel, currentStep === 4 && styles.stepLabelActive]}>
          Review
        </Text>
      </View>
    </View>
  );

  const renderInternshipInfo = () => (
    <LinearGradient
      colors={darkMode ? ['#4a5568', '#2d3748'] : ['#f3f4f6', '#e5e7eb']}
      style={styles.internshipInfo}
    >
      <View style={styles.internshipHeader}>
        <MaterialCommunityIcons name="briefcase" size={20} color="#667eea" />
        <Text style={[styles.internshipTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
          {internship?.title}
        </Text>
      </View>
      <Text style={[styles.internshipCompany, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {internship?.company} • {internship?.location}
      </Text>
      <View style={styles.internshipDetails}>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons name="calendar" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
          <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            {internship?.duration}
          </Text>
        </View>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons name="currency-usd" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
          <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            {internship?.salary}
          </Text>
        </View>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons name="clock-outline" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
          <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Apply by: {internship?.applicationDeadline}
          </Text>
        </View>
      </View>
    </LinearGradient>
  );

  const renderStep1 = () => (
    <View style={styles.stepContainer}>
      <Text style={[styles.stepTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
        Personal Information
      </Text>
      <Text style={[styles.stepDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        Please provide your personal details and educational background.
      </Text>

      <View style={styles.formSection}>
        <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Full Name *
        </Text>
        <TextInput
          ref={nameInputRef}
          style={[
            styles.formInput,
            formErrors.fullName && styles.formInputError,
            { 
              backgroundColor: darkMode ? '#4a5568' : '#ffffff',
              color: darkMode ? '#ffffff' : '#1f2937',
              borderColor: formErrors.fullName ? '#ef4444' : (darkMode ? '#4a5568' : '#d1d5db')
            }
          ]}
          placeholder="Enter your full name"
          value={applicationForm.fullName}
          onChangeText={(text) => {
            setApplicationForm({...applicationForm, fullName: text});
            if (formErrors.fullName) setFormErrors({...formErrors, fullName: ''});
          }}
          placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
        />
        {formErrors.fullName && (
          <Text style={styles.errorText}>{formErrors.fullName}</Text>
        )}
      </View>

      <View style={styles.row}>
        <View style={[styles.formSection, { flex: 1, marginRight: 8 }]}>
          <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Email *
          </Text>
          <TextInput
            ref={emailInputRef}
            style={[
              styles.formInput,
              formErrors.email && styles.formInputError,
              { 
                backgroundColor: darkMode ? '#4a5568' : '#ffffff',
                color: darkMode ? '#ffffff' : '#1f2937',
                borderColor: formErrors.email ? '#ef4444' : (darkMode ? '#4a5568' : '#d1d5db')
              }
            ]}
            placeholder="your.email@example.com"
            keyboardType="email-address"
            autoCapitalize="none"
            value={applicationForm.email}
            onChangeText={(text) => {
              setApplicationForm({...applicationForm, email: text});
              if (formErrors.email) setFormErrors({...formErrors, email: ''});
            }}
            placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
          />
          {formErrors.email && (
            <Text style={styles.errorText}>{formErrors.email}</Text>
          )}
        </View>

        <View style={[styles.formSection, { flex: 1, marginLeft: 8 }]}>
          <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Phone *
          </Text>
          <TextInput
            ref={phoneInputRef}
            style={[
              styles.formInput,
              formErrors.phone && styles.formInputError,
              { 
                backgroundColor: darkMode ? '#4a5568' : '#ffffff',
                color: darkMode ? '#ffffff' : '#1f2937',
                borderColor: formErrors.phone ? '#ef4444' : (darkMode ? '#4a5568' : '#d1d5db')
              }
            ]}
            placeholder="+1 (123) 456-7890"
            keyboardType="phone-pad"
            value={applicationForm.phone}
            onChangeText={(text) => {
              setApplicationForm({...applicationForm, phone: text});
              if (formErrors.phone) setFormErrors({...formErrors, phone: ''});
            }}
            placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
          />
          {formErrors.phone && (
            <Text style={styles.errorText}>{formErrors.phone}</Text>
          )}
        </View>
      </View>

      <View style={styles.formSection}>
        <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          University *
        </Text>
        <TextInput
          style={[
            styles.formInput,
            formErrors.university && styles.formInputError,
            { 
              backgroundColor: darkMode ? '#4a5568' : '#ffffff',
              color: darkMode ? '#ffffff' : '#1f2937',
              borderColor: formErrors.university ? '#ef4444' : (darkMode ? '#4a5568' : '#d1d5db')
            }
          ]}
          placeholder="Your university name"
          value={applicationForm.university}
          onChangeText={(text) => {
            setApplicationForm({...applicationForm, university: text});
            if (formErrors.university) setFormErrors({...formErrors, university: ''});
          }}
          placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
        />
        {formErrors.university && (
          <Text style={styles.errorText}>{formErrors.university}</Text>
        )}
      </View>

      <View style={styles.row}>
        <View style={[styles.formSection, { flex: 2, marginRight: 8 }]}>
          <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Major *
          </Text>
          <TextInput
            style={[
              styles.formInput,
              formErrors.major && styles.formInputError,
              { 
                backgroundColor: darkMode ? '#4a5568' : '#ffffff',
                color: darkMode ? '#ffffff' : '#1f2937',
                borderColor: formErrors.major ? '#ef4444' : (darkMode ? '#4a5568' : '#d1d5db')
              }
            ]}
            placeholder="e.g., Computer Science"
            value={applicationForm.major}
            onChangeText={(text) => {
              setApplicationForm({...applicationForm, major: text});
              if (formErrors.major) setFormErrors({...formErrors, major: ''});
            }}
            placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
          />
          {formErrors.major && (
            <Text style={styles.errorText}>{formErrors.major}</Text>
          )}
        </View>

        <View style={[styles.formSection, { flex: 1, marginLeft: 8 }]}>
          <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            GPA
          </Text>
          <TextInput
            style={[
              styles.formInput,
              { 
                backgroundColor: darkMode ? '#4a5568' : '#ffffff',
                color: darkMode ? '#ffffff' : '#1f2937',
                borderColor: darkMode ? '#4a5568' : '#d1d5db'
              }
            ]}
            placeholder="4.0"
            keyboardType="decimal-pad"
            value={applicationForm.gpa}
            onChangeText={(text) => setApplicationForm({...applicationForm, gpa: text})}
            placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
          />
        </View>

        <View style={[styles.formSection, { flex: 1, marginLeft: 8 }]}>
          <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Grad Year *
          </Text>
          <TextInput
            style={[
              styles.formInput,
              formErrors.graduationYear && styles.formInputError,
              { 
                backgroundColor: darkMode ? '#4a5568' : '#ffffff',
                color: darkMode ? '#ffffff' : '#1f2937',
                borderColor: formErrors.graduationYear ? '#ef4444' : (darkMode ? '#4a5568' : '#d1d5db')
              }
            ]}
            placeholder="2024"
            keyboardType="number-pad"
            value={applicationForm.graduationYear}
            onChangeText={(text) => {
              setApplicationForm({...applicationForm, graduationYear: text});
              if (formErrors.graduationYear) setFormErrors({...formErrors, graduationYear: ''});
            }}
            placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
          />
          {formErrors.graduationYear && (
            <Text style={styles.errorText}>{formErrors.graduationYear}</Text>
          )}
        </View>
      </View>

      <View style={styles.formSection}>
        <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          LinkedIn Profile
        </Text>
        <TextInput
          style={[
            styles.formInput,
            { 
              backgroundColor: darkMode ? '#4a5568' : '#ffffff',
              color: darkMode ? '#ffffff' : '#1f2937',
              borderColor: darkMode ? '#4a5568' : '#d1d5db'
            }
          ]}
          placeholder="https://linkedin.com/in/yourprofile"
          autoCapitalize="none"
          value={applicationForm.linkedin}
          onChangeText={(text) => setApplicationForm({...applicationForm, linkedin: text})}
          placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
        />
      </View>

      <View style={styles.row}>
        <View style={[styles.formSection, { flex: 1, marginRight: 8 }]}>
          <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            GitHub
          </Text>
          <TextInput
            style={[
              styles.formInput,
              { 
                backgroundColor: darkMode ? '#4a5568' : '#ffffff',
                color: darkMode ? '#ffffff' : '#1f2937',
                borderColor: darkMode ? '#4a5568' : '#d1d5db'
            }
            ]}
            placeholder="https://github.com/username"
            autoCapitalize="none"
            value={applicationForm.github}
            onChangeText={(text) => setApplicationForm({...applicationForm, github: text})}
            placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
          />
        </View>

        <View style={[styles.formSection, { flex: 1, marginLeft: 8 }]}>
          <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Portfolio Website
          </Text>
          <TextInput
            style={[
              styles.formInput,
              { 
                backgroundColor: darkMode ? '#4a5568' : '#ffffff',
                color: darkMode ? '#ffffff' : '#1f2937',
                borderColor: darkMode ? '#4a5568' : '#d1d5db'
            }
            ]}
            placeholder="https://yourportfolio.com"
            autoCapitalize="none"
            value={applicationForm.portfolio}
            onChangeText={(text) => setApplicationForm({...applicationForm, portfolio: text})}
            placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
          />
        </View>
      </View>
    </View>
  );

  const renderStep2 = () => (
    <View style={styles.stepContainer}>
      <Text style={[styles.stepTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
        Required Documents
      </Text>
      <Text style={[styles.stepDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        Upload your resume and other supporting documents. Max file size: 10MB each.
      </Text>

      <View style={styles.documentSection}>
        <Text style={[styles.documentTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
          Resume / CV *
        </Text>
        <Text style={[styles.documentSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          PDF, DOC, or DOCX format
        </Text>
        
        {documents.resume ? (
          <View style={[styles.documentCard, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
            <View style={styles.documentInfo}>
              <MaterialCommunityIcons name="file-pdf-box" size={24} color="#ef4444" />
              <View style={styles.documentDetails}>
                <Text style={[styles.documentName, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {documents.resume.name}
                </Text>
                <Text style={[styles.documentSize, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {formatFileSize(documents.resume.size)}
                </Text>
              </View>
            </View>
            <TouchableOpacity 
              style={styles.removeButton}
              onPress={() => removeDocument('resume')}
            >
              <MaterialCommunityIcons name="close-circle" size={20} color="#ef4444" />
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity 
            style={[
              styles.uploadButton,
              formErrors.resume && styles.uploadButtonError,
              { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }
            ]}
            onPress={() => pickDocument('resume')}
          >
            <MaterialCommunityIcons name="upload" size={28} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={[styles.uploadText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              Upload Resume
            </Text>
          </TouchableOpacity>
        )}
        {formErrors.resume && (
          <Text style={styles.errorText}>{formErrors.resume}</Text>
        )}
      </View>

      <View style={styles.documentSection}>
        <Text style={[styles.documentTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
          Academic Transcript (Optional)
        </Text>
        <Text style={[styles.documentSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Unofficial transcript is acceptable
        </Text>
        
        {documents.transcript ? (
          <View style={[styles.documentCard, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
            <View style={styles.documentInfo}>
              <MaterialCommunityIcons name="file-document" size={24} color="#3b82f6" />
              <View style={styles.documentDetails}>
                <Text style={[styles.documentName, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {documents.transcript.name}
                </Text>
                <Text style={[styles.documentSize, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {formatFileSize(documents.transcript.size)}
                </Text>
              </View>
            </View>
            <TouchableOpacity 
              style={styles.removeButton}
              onPress={() => removeDocument('transcript')}
            >
              <MaterialCommunityIcons name="close-circle" size={20} color="#ef4444" />
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity 
            style={[styles.uploadButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
            onPress={() => pickDocument('transcript')}
          >
            <MaterialCommunityIcons name="upload" size={28} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={[styles.uploadText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              Upload Transcript
            </Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.documentSection}>
        <Text style={[styles.documentTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
          Portfolio Samples (Optional)
        </Text>
        <Text style={[styles.documentSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          PDF or ZIP containing your work samples
        </Text>
        
        {documents.portfolioFile ? (
          <View style={[styles.documentCard, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
            <View style={styles.documentInfo}>
              <MaterialCommunityIcons name="folder" size={24} color="#f59e0b" />
              <View style={styles.documentDetails}>
                <Text style={[styles.documentName, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {documents.portfolioFile.name}
                </Text>
                <Text style={[styles.documentSize, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {formatFileSize(documents.portfolioFile.size)}
                </Text>
              </View>
            </View>
            <TouchableOpacity 
              style={styles.removeButton}
              onPress={() => removeDocument('portfolioFile')}
            >
              <MaterialCommunityIcons name="close-circle" size={20} color="#ef4444" />
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity 
            style={[styles.uploadButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
            onPress={() => pickDocument('portfolioFile')}
          >
            <MaterialCommunityIcons name="upload" size={28} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={[styles.uploadText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              Upload Portfolio
            </Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.noteBox}>
        <MaterialCommunityIcons name="information" size={16} color="#f59e0b" />
        <Text style={[styles.noteText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Your resume should be up-to-date and tailored to this position. Include relevant coursework, projects, and experience.
        </Text>
      </View>
    </View>
  );

  const renderStep3 = () => (
    <View style={styles.stepContainer}>
      <Text style={[styles.stepTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
        Professional Details
      </Text>
      <Text style={[styles.stepDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        Tell us why you're the right candidate for this internship.
      </Text>

      <View style={styles.formSection}>
        <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Cover Letter *
        </Text>
        <Text style={[styles.formHint, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Introduce yourself and explain why you're interested in this position.
        </Text>
        <TextInput
          style={[
            styles.formTextarea,
            formErrors.coverLetter && styles.formInputError,
            { 
              backgroundColor: darkMode ? '#4a5568' : '#ffffff',
              color: darkMode ? '#ffffff' : '#1f2937',
              borderColor: formErrors.coverLetter ? '#ef4444' : (darkMode ? '#4a5568' : '#d1d5db'),
              minHeight: 120,
            }
          ]}
          placeholder="Dear Hiring Manager,\n\nI am writing to express my interest in..."
          multiline
          numberOfLines={6}
          textAlignVertical="top"
          value={applicationForm.coverLetter}
          onChangeText={(text) => {
            setApplicationForm({...applicationForm, coverLetter: text});
            if (formErrors.coverLetter) setFormErrors({...formErrors, coverLetter: ''});
          }}
          placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
        />
        {formErrors.coverLetter && (
          <Text style={styles.errorText}>{formErrors.coverLetter}</Text>
        )}
      </View>

      <View style={styles.formSection}>
        <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Why do you want to intern at {internship?.company}? *
        </Text>
        <TextInput
          style={[
            styles.formTextarea,
            formErrors.whyThisCompany && styles.formInputError,
            { 
              backgroundColor: darkMode ? '#4a5568' : '#ffffff',
              color: darkMode ? '#ffffff' : '#1f2937',
              borderColor: formErrors.whyThisCompany ? '#ef4444' : (darkMode ? '#4a5568' : '#d1d5db'),
              minHeight: 80,
            }
          ]}
          placeholder="What excites you about this company and role?"
          multiline
          numberOfLines={4}
          textAlignVertical="top"
          value={applicationForm.whyThisCompany}
          onChangeText={(text) => {
            setApplicationForm({...applicationForm, whyThisCompany: text});
            if (formErrors.whyThisCompany) setFormErrors({...formErrors, whyThisCompany: ''});
          }}
          placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
        />
        {formErrors.whyThisCompany && (
          <Text style={styles.errorText}>{formErrors.whyThisCompany}</Text>
        )}
      </View>

      <View style={styles.formSection}>
        <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Relevant Experience *
        </Text>
        <Text style={[styles.formHint, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Describe projects, coursework, or previous experience related to this role.
        </Text>
        <TextInput
          style={[
            styles.formTextarea,
            formErrors.relevantExperience && styles.formInputError,
            { 
              backgroundColor: darkMode ? '#4a5568' : '#ffffff',
              color: darkMode ? '#ffffff' : '#1f2937',
              borderColor: formErrors.relevantExperience ? '#ef4444' : (darkMode ? '#4a5568' : '#d1d5db'),
              minHeight: 100,
            }
          ]}
          placeholder="• Project X: Developed a web application using React and Node.js\n• Coursework: Advanced Algorithms and Data Structures\n• Previous internship: Software Development at Company Y"
          multiline
          numberOfLines={5}
          textAlignVertical="top"
          value={applicationForm.relevantExperience}
          onChangeText={(text) => {
            setApplicationForm({...applicationForm, relevantExperience: text});
            if (formErrors.relevantExperience) setFormErrors({...formErrors, relevantExperience: ''});
          }}
          placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
        />
        {formErrors.relevantExperience && (
          <Text style={styles.errorText}>{formErrors.relevantExperience}</Text>
        )}
      </View>

      <View style={styles.formSection}>
        <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Technical Skills *
        </Text>
        <Text style={[styles.formHint, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          List your technical skills, programming languages, tools, and frameworks.
        </Text>
        <TextInput
          style={[
            styles.formTextarea,
            formErrors.skills && styles.formInputError,
            { 
              backgroundColor: darkMode ? '#4a5568' : '#ffffff',
              color: darkMode ? '#ffffff' : '#1f2937',
              borderColor: formErrors.skills ? '#ef4444' : (darkMode ? '#4a5568' : '#d1d5db'),
              minHeight: 80,
            }
          ]}
          placeholder="Python, JavaScript, React, Node.js, Git, SQL, AWS, Docker, etc."
          multiline
          numberOfLines={4}
          textAlignVertical="top"
          value={applicationForm.skills}
          onChangeText={(text) => {
            setApplicationForm({...applicationForm, skills: text});
            if (formErrors.skills) setFormErrors({...formErrors, skills: ''});
          }}
          placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
        />
        {formErrors.skills && (
          <Text style={styles.errorText}>{formErrors.skills}</Text>
        )}
      </View>

      <View style={styles.row}>
        <View style={[styles.formSection, { flex: 1, marginRight: 8 }]}>
          <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Availability *
          </Text>
          <TextInput
            style={[
              styles.formInput,
              { 
                backgroundColor: darkMode ? '#4a5568' : '#ffffff',
                color: darkMode ? '#ffffff' : '#1f2937',
                borderColor: darkMode ? '#4a5568' : '#d1d5db'
              }
            ]}
            placeholder="e.g., Summer 2024, Full-time"
            value={applicationForm.availability}
            onChangeText={(text) => setApplicationForm({...applicationForm, availability: text})}
            placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
          />
        </View>

        <View style={[styles.formSection, { flex: 1, marginLeft: 8 }]}>
          <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Salary Expectations
          </Text>
          <TextInput
            style={[
              styles.formInput,
              { 
                backgroundColor: darkMode ? '#4a5568' : '#ffffff',
                color: darkMode ? '#ffffff' : '#1f2937',
                borderColor: darkMode ? '#4a5568' : '#d1d5db'
              }
            ]}
            placeholder="e.g., Market rate"
            value={applicationForm.salaryExpectations}
            onChangeText={(text) => setApplicationForm({...applicationForm, salaryExpectations: text})}
            placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
          />
        </View>
      </View>

      <View style={styles.formSection}>
        <Text style={[styles.formLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          References (Optional)
        </Text>
        <Text style={[styles.formHint, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Name, title, company, email, and phone of 2-3 professional references
        </Text>
        <TextInput
          style={[
            styles.formTextarea,
            { 
              backgroundColor: darkMode ? '#4a5568' : '#ffffff',
              color: darkMode ? '#ffffff' : '#1f2937',
              borderColor: darkMode ? '#4a5568' : '#d1d5db',
              minHeight: 80,
            }
          ]}
          placeholder="Dr. Jane Smith, Professor, University of XYZ\njane.smith@university.edu, +1 (123) 456-7890"
          multiline
          numberOfLines={4}
          textAlignVertical="top"
          value={applicationForm.references}
          onChangeText={(text) => setApplicationForm({...applicationForm, references: text})}
          placeholderTextColor={darkMode ? '#a0aec0' : '#9ca3af'}
        />
      </View>
    </View>
  );

  const renderStep4 = () => (
    <View style={styles.stepContainer}>
      <Text style={[styles.stepTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
        Review & Submit
      </Text>
      <Text style={[styles.stepDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        Please review your application before submitting.
      </Text>

      <View style={styles.reviewSection}>
        <Text style={[styles.reviewTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
          Application Summary
        </Text>
        
        <View style={[styles.reviewCard, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
          <Text style={[styles.reviewItemTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Personal Information
          </Text>
          <Text style={[styles.reviewItem, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {applicationForm.fullName}
          </Text>
          <Text style={[styles.reviewItem, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            {applicationForm.email} • {applicationForm.phone}
          </Text>
          <Text style={[styles.reviewItem, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            {applicationForm.university}, {applicationForm.major} ({applicationForm.graduationYear})
            {applicationForm.gpa ? ` • GPA: ${applicationForm.gpa}` : ''}
          </Text>
        </View>

        <View style={[styles.reviewCard, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
          <Text style={[styles.reviewItemTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Documents
          </Text>
          {documents.resume && (
            <View style={styles.reviewDocument}>
              <MaterialCommunityIcons name="file-pdf-box" size={16} color="#ef4444" />
              <Text style={[styles.reviewItem, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                {documents.resume.name} ({formatFileSize(documents.resume.size)})
              </Text>
            </View>
          )}
          {documents.transcript && (
            <View style={styles.reviewDocument}>
              <MaterialCommunityIcons name="file-document" size={16} color="#3b82f6" />
              <Text style={[styles.reviewItem, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                {documents.transcript.name} ({formatFileSize(documents.transcript.size)})
              </Text>
            </View>
          )}
          {documents.portfolioFile && (
            <View style={styles.reviewDocument}>
              <MaterialCommunityIcons name="folder" size={16} color="#f59e0b" />
              <Text style={[styles.reviewItem, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                {documents.portfolioFile.name} ({formatFileSize(documents.portfolioFile.size)})
              </Text>
            </View>
          )}
          {!documents.resume && !documents.transcript && !documents.portfolioFile && (
            <Text style={[styles.reviewItem, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              No documents uploaded
            </Text>
          )}
        </View>

        <View style={[styles.reviewCard, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
          <Text style={[styles.reviewItemTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Professional Details
          </Text>
          <Text style={[styles.reviewItem, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            Skills: {applicationForm.skills.substring(0, 50)}...
          </Text>
          <Text style={[styles.reviewItem, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Availability: {applicationForm.availability || 'Not specified'}
          </Text>
          <Text style={[styles.reviewItem, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Cover letter: {applicationForm.coverLetter.substring(0, 80)}...
          </Text>
        </View>
      </View>

      <View style={styles.consentSection}>
        <View style={styles.checkboxRow}>
          <TouchableOpacity
            style={[styles.checkbox, { borderColor: darkMode ? '#a0aec0' : '#6b7280' }]}
            onPress={() => setApplicationForm(prev => ({...prev, consent: !prev.consent}))}
          >
            {applicationForm.consent && (
              <MaterialCommunityIcons name="check" size={16} color="#667eea" />
            )}
          </TouchableOpacity>
          <Text style={[styles.consentText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
            I confirm that all information provided is accurate and complete. I understand that any false statements may result in disqualification.
          </Text>
        </View>

        <View style={styles.checkboxRow}>
          <TouchableOpacity
            style={[styles.checkbox, { borderColor: darkMode ? '#a0aec0' : '#6b7280' }]}
            onPress={() => setApplicationForm(prev => ({...prev, privacyConsent: !prev.privacyConsent}))}
          >
            {applicationForm.privacyConsent && (
              <MaterialCommunityIcons name="check" size={16} color="#667eea" />
            )}
          </TouchableOpacity>
          <Text style={[styles.consentText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
            I agree to the processing of my personal data for recruitment purposes in accordance with the privacy policy.
          </Text>
        </View>
      </View>

      <View style={styles.noteBox}>
        <MaterialCommunityIcons name="alert-circle" size={16} color="#f59e0b" />
        <Text style={[styles.noteText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          After submission, you'll receive a confirmation email. You can track your application status in the 'My Applications' section.
        </Text>
      </View>
    </View>
  );

  const renderSuccessModal = () => (
    <Modal
      visible={showSuccessModal}
      animationType="fade"
      transparent={true}
      onRequestClose={() => {
        setShowSuccessModal(false);
        navigation.goBack();
      }}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.successModalContent, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.successIcon}>
            <LinearGradient
              colors={['#10b981', '#34d399']}
              style={styles.successIconGradient}
            >
              <MaterialCommunityIcons name="check" size={48} color="#ffffff" />
            </LinearGradient>
          </View>
          
          <Text style={[styles.successTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            Application Submitted! 🎉
          </Text>
          
          <Text style={[styles.successMessage, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Your application for {internship?.title} at {internship?.company} has been successfully submitted.
          </Text>
          
          <View style={styles.successDetails}>
            <View style={styles.successDetailItem}>
              <MaterialCommunityIcons name="calendar-check" size={20} color="#667eea" />
              <Text style={[styles.successDetailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Submitted: {new Date().toLocaleDateString()}
              </Text>
            </View>
            <View style={styles.successDetailItem}>
              <MaterialCommunityIcons name="clock-outline" size={20} color="#f59e0b" />
              <Text style={[styles.successDetailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Response time: 1-2 weeks
              </Text>
            </View>
            <View style={styles.successDetailItem}>
              <MaterialCommunityIcons name="email" size={20} color="#10b981" />
              <Text style={[styles.successDetailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Confirmation sent to: {applicationForm.email}
              </Text>
            </View>
          </View>
          
          <View style={styles.successActions}>
            <TouchableOpacity 
              style={styles.trackButton}
              onPress={() => {
                setShowSuccessModal(false);
                navigation.navigate('MyApplications');
              }}
            >
              <Text style={styles.trackButtonText}>Track Application</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.doneButton}
              onPress={() => {
                setShowSuccessModal(false);
                navigation.goBack();
              }}
            >
              <Text style={styles.doneButtonText}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return renderStep1();
    }
  };

  if (!internship) {
    return (
      <SafeAreaView style={[styles.safeArea, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
        <View style={styles.errorContainer}>
          <MaterialCommunityIcons name="alert-circle" size={48} color="#ef4444" />
          <Text style={[styles.errorText, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            No internship selected
          </Text>
          <TouchableOpacity 
            style={styles.backButtonFull}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.backButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      <SafeAreaView style={[styles.safeArea, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
        <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
        
        {renderHeader()}
        {renderProgressBar()}
        {renderInternshipInfo()}
        
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardAvoid}
        >
          <ScrollView
            ref={scrollViewRef}
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {renderCurrentStep()}
            
            <View style={styles.bottomPadding} />
          </ScrollView>
        </KeyboardAvoidingView>

        <View style={[styles.footer, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.footerActions}>
            {currentStep > 1 && (
              <TouchableOpacity 
                style={styles.prevButton}
                onPress={handlePrevStep}
              >
                <MaterialCommunityIcons name="arrow-left" size={18} color={darkMode ? '#a0aec0' : '#6b7280'} />
                <Text style={[styles.prevButtonText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Previous
                </Text>
              </TouchableOpacity>
            )}
            
            <View style={{ flex: 1 }} />
            
            {currentStep < totalSteps ? (
              <TouchableOpacity 
                style={styles.nextButton}
                onPress={handleNextStep}
              >
                <Text style={styles.nextButtonText}>Next</Text>
                <MaterialCommunityIcons name="arrow-right" size={18} color="#ffffff" />
              </TouchableOpacity>
            ) : (
              <TouchableOpacity 
                style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
                onPress={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <>
                    <Text style={styles.submitButtonText}>Submit Application</Text>
                    <MaterialCommunityIcons name="send" size={18} color="#ffffff" />
                  </>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>

        {renderSuccessModal()}
      </SafeAreaView>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 8,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  saveButton: {
    padding: 8,
  },
  progressContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'transparent',
  },
  progressBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepCompleted: {
    backgroundColor: '#10b981',
  },
  stepActive: {
    backgroundColor: '#667eea',
  },
  stepInactive: {
    backgroundColor: '#e5e7eb',
  },
  stepNumber: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  stepNumberActive: {
    color: '#ffffff',
  },
  stepNumberInactive: {
    color: '#6b7280',
  },
  stepLine: {
    width: 60,
    height: 2,
    marginHorizontal: 4,
  },
  stepLineCompleted: {
    backgroundColor: '#10b981',
  },
  stepLineInactive: {
    backgroundColor: '#e5e7eb',
  },
  stepLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    marginTop: 8,
  },
  stepLabel: {
    fontSize: 10,
    color: '#9ca3af',
    textAlign: 'center',
    width: 60,
  },
  stepLabelActive: {
    color: '#667eea',
    fontWeight: '600',
  },
  internshipInfo: {
    marginHorizontal: 16,
    marginBottom: 12,
    padding: 12,
    borderRadius: 10,
  },
  internshipHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  internshipTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  internshipCompany: {
    fontSize: 13,
    marginBottom: 8,
  },
  internshipDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailText: {
    fontSize: 11,
    marginLeft: 4,
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 100,
  },
  stepContainer: {
    marginTop: 16,
  },
  stepTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  stepDescription: {
    fontSize: 14,
    marginBottom: 20,
  },
  formSection: {
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 6,
  },
  formHint: {
    fontSize: 12,
    marginBottom: 6,
  },
  formInput: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
  },
  formInputError: {
    borderColor: '#ef4444',
  },
  formTextarea: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
  },
  row: {
    flexDirection: 'row',
  },
  errorText: {
    fontSize: 12,
    color: '#ef4444',
    marginTop: 4,
  },
  documentSection: {
    marginBottom: 20,
  },
  documentTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  documentSubtitle: {
    fontSize: 12,
    marginBottom: 12,
  },
  uploadButton: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 8,
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadButtonError: {
    borderColor: '#ef4444',
  },
  uploadText: {
    fontSize: 14,
    marginTop: 8,
  },
  documentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
  },
  documentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  documentDetails: {
    marginLeft: 12,
    flex: 1,
  },
  documentName: {
    fontSize: 14,
    fontWeight: '500',
  },
  documentSize: {
    fontSize: 12,
    marginTop: 2,
  },
  removeButton: {
    padding: 4,
  },
  noteBox: {
    flexDirection: 'row',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    padding: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  noteText: {
    fontSize: 12,
    marginLeft: 8,
    flex: 1,
  },
  reviewSection: {
    marginBottom: 24,
  },
  reviewTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  reviewCard: {
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
  },
  reviewItemTitle: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
  },
  reviewItem: {
    fontSize: 13,
    marginBottom: 4,
  },
  reviewDocument: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  consentSection: {
    marginBottom: 24,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    marginRight: 12,
    marginTop: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  consentText: {
    fontSize: 13,
    flex: 1,
  },
  footer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  footerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  prevButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  prevButtonText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  nextButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  nextButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginRight: 6,
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  submitButtonDisabled: {
    backgroundColor: '#9ca3af',
  },
  submitButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginRight: 6,
  },
  bottomPadding: {
    height: 80,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  successModalContent: {
    width: '100%',
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
  },
  successIcon: {
    marginBottom: 20,
  },
  successIconGradient: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  successTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 12,
    textAlign: 'center',
  },
  successMessage: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
  },
  successDetails: {
    width: '100%',
    marginBottom: 24,
  },
  successDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  successDetailText: {
    fontSize: 13,
    marginLeft: 8,
    flex: 1,
  },
  successActions: {
    flexDirection: 'row',
    width: '100%',
    gap: 12,
  },
  trackButton: {
    flex: 1,
    backgroundColor: '#667eea',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  trackButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  doneButton: {
    flex: 1,
    backgroundColor: '#e5e7eb',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  doneButtonText: {
    color: '#6b7280',
    fontSize: 14,
    fontWeight: '600',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 24,
  },
  backButtonFull: {
    backgroundColor: '#667eea',
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
  },
  backButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default InternshipsApply;